/**
 * persistence/hooks/useSessionPersistence.js
 *
 * The main integration hook.  Mount this ONCE in InputBoard (and its variants)
 * to wire up the entire persistence system for the active session.
 *
 * What this hook does:
 *   1. Verifies IndexedDB is available (degrades gracefully on failure)
 *   2. Initialises CheckpointManager state from IndexedDB (survives refresh)
 *   3. Runs pruning to keep IndexedDB size bounded
 *   4. Checks for crash recovery and returns a recovery offer if found
 *   5. Starts SyncManager (REST API sync loop)
 *   6. Starts Firestore sync loop
 *   7. Registers visibilitychange + beforeunload handlers for exit saves
 *   8. Returns sync status + recovery state for the UI
 *
 * Usage:
 *   const { recoveryOffer, dismissRecovery, restoreFromRecovery } =
 *     useSessionPersistence({ matchId, analystId, token, onRestoreString });
 *
 * The hook is intentionally narrow — it manages lifecycle only.
 * Actual IndexedDB writes happen in the Redux middleware, not here.
 *
 * @param {object}   params
 * @param {string}   params.matchId          From useParams()
 * @param {string}   params.analystId        From Redux auth state
 * @param {string}   params.token            From Redux auth state
 * @param {function} params.onRestoreString  Callback called with the recovered
 *                                           string when analyst clicks "Restore"
 */

import { useEffect, useState, useCallback, useRef } from "react";
import toast from "react-hot-toast";

import { checkDatabaseHealth }          from "../db/database";
import { initCheckpointState, pruneVersions, createExitCheckpoint } from "../managers/CheckpointManager";
import { pruneDiffLog }                 from "../managers/DiffManager";
import { flushSessionWrite, endSession } from "../managers/SessionManager";
import { pruneCompletedQueueItems, startSyncManager, stopSyncManager, flushNow } from "../managers/SyncManager";
import { checkForRecovery }             from "../managers/RecoveryManager";
import { runStaleDataCleanup }          from "../managers/CleanupManager";
import { startFirestoreSync, stopFirestoreSync, flushFirestoreQueue } from "../firestore/firestoreSync";

export function useSessionPersistence({ matchId, analystId, token, onRestoreString }) {
  const [dbAvailable,   setDbAvailable]   = useState(true);
  const [recoveryOffer, setRecoveryOffer] = useState(null); // RecoveryOffer | null
  const [isRestoring,   setIsRestoring]   = useState(false);

  // Keep a ref to the latest string so beforeunload can access it
  const latestStringRef = useRef("");

  // ── Expose a setter so InputBoard can keep latestStringRef in sync ─────────
  const setLatestString = useCallback((str) => {
    latestStringRef.current = str;
  }, []);

  // ── Main mount effect ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!matchId || !analystId) return;

    let mounted = true;

    async function init() {
      // 1. Verify IndexedDB
      const healthy = await checkDatabaseHealth();
      if (!mounted) return;

      if (!healthy) {
        setDbAvailable(false);
        console.warn("[useSessionPersistence] IndexedDB unavailable — REST-only mode");
        // SyncManager still starts, will use direct axios without IndexedDB queue
        startSyncManager({
          onSyncError:   (msg) =>{}, //toast.error(msg, { id: "sync-error", duration: 8000 }),
          onSyncSuccess: () => {},
        });
        return;
      }

      // 2. Initialise checkpoint state from IndexedDB (restore after refresh)
      await initCheckpointState(matchId, analystId);

      // 3. Prune old records (runs in background, non-blocking)
      //    pruneVersions / pruneDiffLog cap the CURRENT session.
      //    runStaleDataCleanup handles all OTHER old sessions — it is throttled
      //    internally to run at most once per 24 h so it is safe to call here.
      Promise.all([
        pruneVersions(matchId, analystId),
        pruneDiffLog(matchId, analystId),
        pruneCompletedQueueItems(),
        runStaleDataCleanup(),
      ]).catch((e) => console.warn("[useSessionPersistence] Pruning error:", e.message));

      // 4. Check for crash recovery
      const offer = await checkForRecovery(matchId, analystId);
      if (!mounted) return;

      if (offer.shouldRecover) {
        setRecoveryOffer(offer);
        // Don't show a toast here — the parent component renders the recovery UI
      }

      // 5. Start REST API SyncManager
      startSyncManager({
        onSyncError: (msg) =>{},
          // toast.error(msg, { id: "sync-error", duration: 8000 }),
        onSyncSuccess: () =>{},
          // toast.success("Sync to backup history", { id: "sync-ok", duration: 2000 }),
        onStaleSave: (msg) => toast.error(msg, { id: "sync-stale", duration: 10000 }),
      });

      // 6. Start Firestore sync loop
      startFirestoreSync();
    }

    init();

    // ── Exit handlers ────────────────────────────────────────────────────────

    const handleVisibilityChange = async () => {
      if (document.visibilityState === "hidden") {
        // Tab is being hidden / switched away — flush everything
        if (latestStringRef.current && matchId && analystId) {
          await flushSessionWrite({
            matchId,
            analystId,
            currentString: latestStringRef.current,
          });
          // Guarantee a cloud snapshot of the final state (only if work changed
          // since the last cloud write) before flushing the queue.
          await createExitCheckpoint({
            matchId,
            analystId,
            currentString: latestStringRef.current,
          });
        }
        await flushNow();
        await flushFirestoreQueue();
      }
    };

    const handleBeforeUnload = (e) => {
      // beforeunload: synchronous path — fire-and-forget async work
      // IndexedDB and fetch APIs are best-effort here; the browser may not
      // wait for async work to complete, but modern browsers do for a short window.
      if (latestStringRef.current && matchId && analystId) {
        flushSessionWrite({
          matchId,
          analystId,
          currentString: latestStringRef.current,
        });
        // Best-effort cloud snapshot on close (visibilitychange is the reliable
        // path; this is fire-and-forget since beforeunload can't await).
        createExitCheckpoint({
          matchId,
          analystId,
          currentString: latestStringRef.current,
        });
        endSession(matchId, analystId);
      }
      flushNow();
      flushFirestoreQueue();
      // Do NOT call e.preventDefault() — we removed that from InputBoard so
      // the browser doesn't block the unload dialog.
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      mounted = false;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      stopSyncManager();
      stopFirestoreSync();
    };
  }, [matchId, analystId]); // eslint-disable-line react-hooks/exhaustive-deps
  // token intentionally excluded — SyncManager reads token per-call from the queue

  // ── Recovery actions ───────────────────────────────────────────────────────

  const restoreFromRecovery = useCallback(() => {
    if (!recoveryOffer?.localString) return;
    setIsRestoring(true);
    onRestoreString(recoveryOffer.localString);
    setRecoveryOffer(null);
    setIsRestoring(false);
    toast.success(
      `Session restored — ${recoveryOffer.localEventCount} events recovered.`,
      { duration: 4000 }
    );
  }, [recoveryOffer, onRestoreString]);

  const dismissRecovery = useCallback(() => {
    setRecoveryOffer(null);
  }, []);

  return {
    /** true if IndexedDB is functioning */
    dbAvailable,
    /** RecoveryOffer object if there is unsaved local work, otherwise null */
    recoveryOffer,
    /** Call to restore the local string into Redux/InputBoard state */
    restoreFromRecovery,
    /** Call to dismiss the recovery offer without restoring */
    dismissRecovery,
    /** true while the restore operation is in progress */
    isRestoring,
    /** Call this every time fullstringanalyse changes to keep exit handlers current */
    setLatestString,
  };
}
